import React from 'react';
import { type TFunction } from 'i18next';

import { type Option } from 'src/types';

export type TranslateOptionLabel = (option: Option<unknown>) => string;

/**
 * Convenience method to automatically translate labels of select options. Do not use it for hard-coded
 * translation values - this is to be used mainly for translating data that come from the back-end.
 * @example
 * const { t } = useTranslation(['backend', 'country']);
 * const translateCountryOptionLabel = useTranslateOptionLabel(t, 'country');
 * <Select
 *   formatOptionLabel={translateCountryOptionLabel}
 *   ...
 * />
 * @param t - TFunction from useTranslation. This is external to keep translation namespaces external.
 * @param namespace - Namespace to use for translation. If not provided, the default namespace is used.
 * @returns a function to be passed to `formatOptionLabel` prop of Select components
 */
export const useTranslateOptionLabel = (
  t: TFunction,
  namespace?: string,
  // Remove this suppression after removing the suppression below
) => {
  // eslint-disable-next-line no-restricted-syntax
  return React.useCallback<TranslateOptionLabel>((option) => t(option.label, { ns: namespace }), [t, namespace]);
};
